import { useEffect, useRef } from "react";
import { Button, HStack } from "@chakra-ui/react";
import { useAppStore } from "../../../context/store";
import { Tooltip } from "../../ui/tooltip";
import { LuPencilRuler } from "react-icons/lu";
import { handleDrawLineWithIntersections } from "../../../utilies/other/LineIntersectMeasureMode";
import Type from "../../../utilies/Types";
import { getCombinedKeysText } from "../../../utilies/Utility";

function LineIntersectMeasure() {
  const viewerRef = useAppStore((s) => s.viewerRef);
  const lineIntersectMeasureMode = useAppStore((s) => s.lineIntersectMeasureMode);
  const originalCameraControls = useAppStore((s) => s.originalCameraControls);
  const savedHotkeysRef = useAppStore((s) => s.savedHotkeysRef);
  const setLineIntersectMeasureMode = useAppStore((s) => s.setLineIntersectMeasureMode);
  const setMeasureDistanceMode = useAppStore((s) => s.setMeasureDistanceMode);
  const setOverlayTextMode = useAppStore.getState().setOverlayTextMode;

  const toolRef = useRef(null);

  useEffect(() => {
    const isEditable = (el) => {
      if (!el) return false;
      const n = (el.tagName || "").toLowerCase();
      return n === "input" || n === "textarea" || el.isContentEditable === true;
    };

    const isMod = (k) => ["shift", "ctrl", "alt", "meta"].includes(String(k).toLowerCase());
    const isAlpha = (k) => /^[a-z]$/i.test(String(k || ""));
    const normalizeKey = (k) => {
      let key = String(k || "").toLowerCase();
      if (key === "control") key = "ctrl";
      if (key === " ") key = "space";
      return key;
    };

    const getCombo = (type) => savedHotkeysRef.current.find((h) => h.type === type)?.combination || [];

    const matches = (e, combo) => {
      if (!combo || combo.length === 0) return false;

      const norm = combo.map(normalizeKey);

      const want = {
        shift: norm.includes("shift"),
        ctrl: norm.includes("ctrl"),
        alt: norm.includes("alt"),
        meta: norm.includes("meta"),
      };

      if (e.shiftKey !== want.shift) return false;
      if (e.ctrlKey !== want.ctrl) return false;
      if (e.altKey !== want.alt) return false;
      if (e.metaKey !== want.meta) return false;

      const main = norm.find((k) => !isMod(k));
      if (!main) return true;

      return normalizeKey(e.key) === main;
    };

    // -------------------------
    // 3-key sequential support
    // combo: [mod, letter1, letter2]
    // user: mod+letter1, then within 600ms press letter2 (with same mods or no mods)
    // -------------------------
    const PENDING_KEY = "__line_intersect_hotkey_pending__";
    if (!window[PENDING_KEY]) {
      window[PENDING_KEY] = {
        active: false,
        mods: [],
        stepKey: "",
        finalKey: "",
        expiresAt: 0,
        tid: null,
      };
    }
    const pending = window[PENDING_KEY];

    const resetPending = () => {
      if (pending.tid) clearTimeout(pending.tid);
      pending.active = false;
      pending.mods = [];
      pending.stepKey = "";
      pending.finalKey = "";
      pending.expiresAt = 0;
      pending.tid = null;
    };

    const modsMatchExactly = (e, mods) => {
      const needShift = mods.includes("shift");
      const needCtrl = mods.includes("ctrl");
      const needAlt = mods.includes("alt");
      const needMeta = mods.includes("meta");

      if (e.shiftKey !== needShift) return false;
      if (e.ctrlKey !== needCtrl) return false;
      if (e.altKey !== needAlt) return false;
      if (e.metaKey !== needMeta) return false;
      return true;
    };

    const modsAreNone = (e) => !e.shiftKey && !e.ctrlKey && !e.altKey && !e.metaKey;

    const isSequential3Combo = (combo) => {
      if (!combo || combo.length !== 3) return false;

      const norm = combo.map(normalizeKey);
      const mods = norm.filter(isMod);
      const nonMods = norm.filter((k) => !isMod(k));

      return mods.length === 1 && nonMods.length === 2 && isAlpha(nonMods[0]) && isAlpha(nonMods[1]);
    };

    const getSequentialParts = (combo) => {
      const norm = combo.map(normalizeKey);
      const mods = norm.filter(isMod);
      const nonMods = norm.filter((k) => !isMod(k));
      return { mods, stepKey: nonMods[0], finalKey: nonMods[1] };
    };

    const startPending = (mods, stepKey, finalKey) => {
      resetPending();
      pending.active = true;
      pending.mods = mods;
      pending.stepKey = stepKey;
      pending.finalKey = finalKey;
      pending.expiresAt = Date.now() + 3000;
      pending.tid = setTimeout(() => resetPending(), 3000);
    };

    const onKey = (e) => {
      if (e.repeat) return;
      if (isEditable(e.target)) return;

      const combo = getCombo(Type.HOTKEYS.LINE_INTERSECT_MEASURE);
      const key = normalizeKey(e.key);

      // 1) pending final key check
      if (pending.active) {
        if (Date.now() > pending.expiresAt) {
          resetPending();
        } else {
          const okMods = modsMatchExactly(e, pending.mods) || modsAreNone(e);

          if (okMods && key === normalizeKey(pending.finalKey)) {
            e.preventDefault();
            resetPending();
            setLineIntersectMeasureMode((v) => !v);
            return;
          }

          if (!isMod(key)) resetPending();
        }
      }

      // 2) sequential start
      if (isSequential3Combo(combo)) {
        const { mods, stepKey, finalKey } = getSequentialParts(combo);

        if (modsMatchExactly(e, mods) && key === normalizeKey(stepKey)) {
          e.preventDefault();
          startPending(mods, stepKey, finalKey);
          return;
        }
      }

      // 3) normal one-shot
      if (matches(e, combo)) {
        e.preventDefault();
        resetPending();
        setLineIntersectMeasureMode((v) => !v);
      }
    };

    window.addEventListener("keydown", onKey, true);
    return () => {
      window.removeEventListener("keydown", onKey, true);
      resetPending();
    };
  }, [setLineIntersectMeasureMode]);

  useEffect(() => {
    if (lineIntersectMeasureMode) {
      if (!toolRef.current && viewerRef?.current) {
        toolRef.current = handleDrawLineWithIntersections(
          viewerRef.current,
          originalCameraControls,
          ["lane_marker", "virtual_line", "road_edge", "curbstone", "guard_rail", "concrete_barrier", "stop_line", "yield_line"],
          { onDeactivate: () => setLineIntersectMeasureMode(false) },
        );
      }
      setMeasureDistanceMode(false);
      setOverlayTextMode(false);
    } else {
      if (toolRef.current) {
        toolRef.current.destroy();
        toolRef.current = null;
      }
    }
    return () => {
      if (toolRef.current) {
        toolRef.current.destroy();
        toolRef.current = null;
      }
    };
  }, [lineIntersectMeasureMode]);

  return (
    <HStack>
      <Button
        variant="solid"
        p={0}
        opacity={1}
        bg={lineIntersectMeasureMode ? "green" : "white"}
        color={lineIntersectMeasureMode ? "white" : "MenuText"}
        onClick={() => setLineIntersectMeasureMode(!lineIntersectMeasureMode)}
      >
        <Tooltip content={"Line Intersect Measure " + getCombinedKeysText(savedHotkeysRef, Type.HOTKEYS.LINE_INTERSECT_MEASURE)}>
          <LuPencilRuler />
        </Tooltip>
      </Button>
    </HStack>
  );
}

export default LineIntersectMeasure;
